import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  ArrowLeft,
  Users,
  Truck,
  ShoppingCart,
  Mail,
  Phone,
  UserRound,
  MapPin,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Box } from '@mui/material';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminRestaurantDetail,
  type SuperAdminUserSummary
} from '@/pages/SuperAdmin/api/superAdminApi';
import { SaDataTable, type SaColumn } from '@/pages/SuperAdmin/SaDataTable';
import { SaNeutralChip, SaStatusChip } from '@/pages/SuperAdmin/SaStatusChip';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';

type TabId = 'overview' | 'employees' | 'suppliers' | 'locations' | 'orders';

type EmployeeRow = SuperAdminUserSummary & {
  locationId: string | null;
  locationName: string | null;
};
type SupplierRow = SuperAdminUserSummary & {
  linkedAt: string;
  networkType: 'in_network' | 'out_of_network';
};
type LocationRow = SuperAdminRestaurantDetail['locations'][number];
type OrderRow = SuperAdminRestaurantDetail['orders']['items'][number];
type SupplierNetworkFilter = 'all' | 'in_network' | 'out_of_network';

export const SuperAdminRestaurantDetailPage: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [detail, setDetail] = useState<SuperAdminRestaurantDetail | null>(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [supplierNetworkFilter, setSupplierNetworkFilter] =
    useState<SupplierNetworkFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!accessToken || !restaurantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await superAdminApi.getRestaurantDetail(
        accessToken,
        restaurantId,
        {
          ordersPage,
          ordersLimit: 20
        }
      );
      setDetail(res.data);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to load restaurant';
      setError(Array.isArray(message) ? message.join(', ') : String(message));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, restaurantId, ordersPage]);

  useEffect(() => {
    load();
  }, [load]);

  const employeeColumns = useMemo<SaColumn<EmployeeRow>[]>(
    () => [
      {
        id: 'employee',
        label: 'Employee',
        width: '26%',
        render: row => {
          const name =
            [row.firstName, row.lastName].filter(Boolean).join(' ') ||
            row.businessName ||
            '—';
          return (
            <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{name}</Box>
          );
        }
      },
      {
        id: 'email',
        label: 'Email',
        width: '24%',
        render: row => row.email || '—'
      },
      {
        id: 'phone',
        label: 'Phone',
        width: '14%',
        render: row => row.phone || '—'
      },
      {
        id: 'location',
        label: 'Location',
        width: '18%',
        render: row => row.locationName || '—'
      },
      {
        id: 'status',
        label: 'Status',
        width: '18%',
        render: row => <SaStatusChip status={row.status} />
      }
    ],
    []
  );

  const supplierColumns = useMemo<SaColumn<SupplierRow>[]>(
    () => [
      {
        id: 'supplier',
        label: 'Supplier',
        width: '28%',
        render: row =>
          row.businessName ||
          [row.firstName, row.lastName].filter(Boolean).join(' ') ||
          '—'
      },
      {
        id: 'email',
        label: 'Email',
        width: '26%',
        render: row => row.email || '—'
      },
      {
        id: 'network',
        label: 'Network',
        width: '18%',
        render: row => (
          <SaNeutralChip
            width={120}
            label={
              row.networkType === 'out_of_network'
                ? 'Out of network'
                : 'In network'
            }
          />
        )
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'linked',
        label: 'Linked',
        width: '14%',
        render: row =>
          row.linkedAt ? new Date(row.linkedAt).toLocaleDateString() : '—'
      }
    ],
    []
  );

  const locationColumns = useMemo<SaColumn<LocationRow>[]>(
    () => [
      {
        id: 'name',
        label: 'Location',
        width: '28%',
        render: row => (
          <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {row.name || '—'}
            {row.isDefault && (
              <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                <SaNeutralChip label="Default" />
              </Box>
            )}
          </Box>
        )
      },
      {
        id: 'address',
        label: 'Address',
        width: '36%',
        render: row =>
          row.formattedAddress ||
          [row.city, row.state].filter(Boolean).join(', ') ||
          '—'
      },
      {
        id: 'suppliers',
        label: 'Suppliers',
        width: '14%',
        align: 'center',
        render: row => row.supplierCount
      },
      {
        id: 'status',
        label: 'Status',
        width: '22%',
        render: row => (
          <SaStatusChip
            status={row.isActive ? 'active' : 'inactive'}
            label={row.isActive ? 'active' : 'inactive'}
          />
        )
      }
    ],
    []
  );

  const orderColumns = useMemo<SaColumn<OrderRow>[]>(
    () => [
      {
        id: 'orderNumber',
        label: 'Order #',
        width: '16%',
        render: row => (
          <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{row.orderNumber}</Box>
        )
      },
      {
        id: 'name',
        label: 'Name',
        width: '22%',
        render: row => row.orderName || '—'
      },
      {
        id: 'supplier',
        label: 'Supplier',
        width: '26%',
        render: row => row.supplierName || '—'
      },
      {
        id: 'status',
        label: 'Status',
        width: '16%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'created',
        label: 'Created',
        width: '20%',
        render: row =>
          row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'
      }
    ],
    []
  );

  if (loading && !detail) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">{error}</Alert>
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  if (!detail) return null;

  const { restaurant, employees, suppliers, locations, orders, counts } =
    detail;
  const filteredSuppliers =
    supplierNetworkFilter === 'all'
      ? suppliers
      : suppliers.filter(s => s.networkType === supplierNetworkFilter);
  const inNetworkCount = suppliers.filter(
    s => s.networkType === 'in_network'
  ).length;
  const outOfNetworkCount = suppliers.filter(
    s => s.networkType === 'out_of_network'
  ).length;
  const displayName =
    restaurant.businessName ||
    [restaurant.firstName, restaurant.lastName].filter(Boolean).join(' ') ||
    'Restaurant';

  const tabs: {
    id: TabId;
    label: string;
    count?: number;
    icon: React.ReactNode;
  }[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <UserRound className="h-4 w-4" />
    },
    {
      id: 'employees',
      label: 'Employees',
      count: counts.employees,
      icon: <Users className="h-4 w-4" />
    },
    {
      id: 'suppliers',
      label: 'Suppliers',
      count: counts.suppliers,
      icon: <Truck className="h-4 w-4" />
    },
    {
      id: 'locations',
      label: 'Locations',
      count: counts.locations,
      icon: <MapPin className="h-4 w-4" />
    },
    {
      id: 'orders',
      label: 'Orders',
      count: counts.orders,
      icon: <ShoppingCart className="h-4 w-4" />
    }
  ];

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/super-admin/restaurants"
          className="mb-2 inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-[#006B82]"
        >
          <ArrowLeft className="h-4 w-4" />
          Restaurants
        </Link>
        <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
          {displayName}
        </h2>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-tertiary">
          <SaStatusChip status={restaurant.status} />
          {restaurant.email && (
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" />
              {restaurant.email}
            </span>
          )}
          {restaurant.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" />
              {restaurant.phone}
            </span>
          )}
        </p>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      <div className="flex flex-wrap gap-1 border-b border-border-primary">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={[
              'inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-[#006B82] text-[#006B82]'
                : 'border-transparent text-text-tertiary hover:text-text-primary'
            ].join(' ')}
          >
            {t.icon}
            {t.label}
            {typeof t.count === 'number' && (
              <span className="rounded-full bg-background-tertiary px-2 py-0.5 text-xs text-text-secondary">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Employees</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.employees}
            </p>
          </div>
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Suppliers</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.suppliers}
            </p>
          </div>
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Locations</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.locations}
            </p>
          </div>
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Orders</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.orders}
            </p>
          </div>
          <div className="sm:col-span-2 lg:col-span-4 rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <h3 className="font-semibold text-text-primary">Account</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  First name
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {restaurant.firstName || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Last name
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {restaurant.lastName || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Role
                </p>
                <p className="mt-1 font-medium capitalize text-text-primary">
                  {restaurant.role?.replace(/_/g, ' ') || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Source
                </p>
                <p className="mt-1 font-medium capitalize text-text-primary">
                  {restaurant.source || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Invite
                </p>
                <p className="mt-1 font-medium capitalize text-text-primary">
                  {restaurant.inviteStatus || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Created
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {restaurant.createdAt
                    ? new Date(restaurant.createdAt).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'employees' && (
        <SaDataTable
          columns={employeeColumns}
          rows={employees}
          getRowId={row => row.id}
          emptyMessage="No employees linked to this restaurant"
        />
      )}

      {tab === 'suppliers' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-border-primary bg-background-card p-4 shadow-sm">
            <div>
              <label className="mb-1 block text-xs font-medium text-text-tertiary">
                Network
              </label>
              <select
                className="block h-10 rounded-md border border-border-primary bg-background-card px-3 text-sm"
                value={supplierNetworkFilter}
                onChange={e =>
                  setSupplierNetworkFilter(
                    e.target.value as SupplierNetworkFilter
                  )
                }
              >
                <option value="all">
                  All ({suppliers.length})
                </option>
                <option value="in_network">
                  In network ({inNetworkCount})
                </option>
                <option value="out_of_network">
                  Out of network ({outOfNetworkCount})
                </option>
              </select>
            </div>
            <p className="text-sm text-text-tertiary">
              Showing {filteredSuppliers.length} of {suppliers.length}
            </p>
          </div>
          <SaDataTable
            columns={supplierColumns}
            rows={filteredSuppliers}
            getRowId={row => row.id}
            emptyMessage={
              supplierNetworkFilter === 'all'
                ? 'No suppliers linked to this restaurant'
                : supplierNetworkFilter === 'in_network'
                  ? 'No in-network suppliers'
                  : 'No out-of-network suppliers'
            }
          />
        </div>
      )}

      {tab === 'locations' && (
        <SaDataTable
          columns={locationColumns}
          rows={locations}
          getRowId={row => row.id}
          emptyMessage="No locations for this restaurant"
        />
      )}

      {tab === 'orders' &&
        (loading ? (
          <div className="flex justify-center rounded-2xl border border-border-primary bg-background-card py-12 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-[#006B82]" />
          </div>
        ) : (
          <div className="space-y-3">
            <SaDataTable
              columns={orderColumns}
              rows={orders.items}
              getRowId={row => row.id}
              emptyMessage="No orders for this restaurant"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-text-secondary">
              <span>
                {orders.total} order{orders.total === 1 ? '' : 's'} · Page{' '}
                {orders.page} of {orders.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={orders.page <= 1}
                  onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={orders.page >= orders.totalPages}
                  onClick={() => setOrdersPage(p => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};
